/**
 * İşaretçi kataloğu — tuvale konan simgelerin tek listesi.
 *
 * **Wardogs kataloğu.** Liste bir zamanlar Hell Let Loose'un simgeleriydi
 * (garrison, airhead, outpost…). Şimdiki kavramlar oyunun ve savaş odası
 * araçlarının kullandıkları: tarafların FOB'ları, LZ, toplanma noktası,
 * paraşütle indirme, araçlar ve ikmal türleri.
 *
 * Simgeler **bizim çizimimiz** (`public/markers/wd/*.svg`, 24'lük ızgara;
 * araçlar ayrıntı için 48'lik ızgarada `scale(.5)` ile):
 * kemik beyazı dolgu + siyah kontur, kule simgesiyle aynı dil — uydu zemininde
 * de taktiksel zeminde de okunur. Renk işaretçinin altındaki üçgendedir; düşman
 * ve FOB simgeleri istisna: düşman kızıl, FOB'lar tarafın renginde dolgulu —
 * renk seçilmeden de ayırt edilsinler.
 *
 * Eski HLL dosyaları `public/markers/` altında, kataloğdan çıkan Wardogs
 * simgeleri de (`smoke`, `support`, `soldier`, `heli-drop`, `parachute`, sınıflar…) `wd/` altında **duruyor**: kayıtlı slaytlar
 * işaretçiyi dosya yoluyla (`src`) saklıyor, silinirlerse eski planlarda kırık
 * görsel kalırdı. Yalnızca seçiciden çıktılar.
 *
 * Adlar oyunun terimleri — İngilizce kalır, çevrilmez (arama da onlarla).
 */
export type MarkerGroup = 'tactical' | 'vehicles' | 'supply'

const wd = (id: string) => `/markers/wd/${id}.svg`

export const MARKERS = [
  { id: 'objective',     group: 'tactical', label: 'Objective',     src: wd('objective') },
  { id: 'fob-lonestar',  group: 'tactical', label: 'Lonestar FOB',  src: wd('fob-lonestar') },
  { id: 'fob-valkyra',   group: 'tactical', label: 'Valkyra FOB',   src: wd('fob-valkyra') },
  { id: 'fob-manticore', group: 'tactical', label: 'Manticore FOB', src: wd('fob-manticore') },
  { id: 'enemy',         group: 'tactical', label: 'Enemy',         src: wd('enemy') },
  { id: 'paradrop',      group: 'tactical', label: 'Parachute Drop', src: wd('paratrooper') },
  { id: 'rally',         group: 'tactical', label: 'Rally',         src: wd('rally') },
  { id: 'lz',            group: 'tactical', label: 'LZ',            src: wd('lz') },
  { id: 'attack',        group: 'tactical', label: 'Attack',        src: wd('attack') },
  { id: 'defend',        group: 'tactical', label: 'Defend',        src: wd('defend') },
  { id: 'danger',        group: 'tactical', label: 'Danger',        src: wd('danger') },
  { id: 'note',          group: 'tactical', label: 'Note',          src: wd('note') },

  { id: 'tank',          group: 'vehicles', label: 'Tank',          src: wd('tank-side') },
  { id: 'helicopter',    group: 'vehicles', label: 'Helicopter',    src: wd('heli-side') },
  { id: 'truck',         group: 'vehicles', label: 'Truck',         src: wd('truck-side') },

  { id: 'ammo',          group: 'supply',   label: 'Ammo',          src: wd('ammo') },
  { id: 'fuel',          group: 'supply',   label: 'Fuel',          src: wd('fuel') },
  { id: 'mechanical',    group: 'supply',   label: 'Mechanical',    src: wd('tires') },
  { id: 'build',         group: 'supply',   label: 'Build',         src: wd('build') },
] as const satisfies readonly { id: string; group: MarkerGroup; label: string; src: string }[]

export type MarkerDef = (typeof MARKERS)[number]

export const MARKER_GROUPS: MarkerGroup[] = ['tactical', 'vehicles', 'supply']

/**
 * FOB'lar — konunca **alanıyla** gelir.
 *
 * Alan 120 × 120 m'lik bir kare (merkezden 60 m): ölçü wardogs.tech savaş
 * odasındaki FOB bölgesinden (`fob-zone`, 60 m yarı kenar). Orada kare hep
 * kuzeye bakıyor; bizde döndürülebilir (`PlacedMarker.rotation`, derece),
 * çünkü FOB'un sahadaki yönü planın parçası.
 *
 * Renk tarafın rengidir — simgenin dolgusu, alanın kenarı ve işaretçinin
 * üçgeni aynı renkte. Tonlar savaş odasının takım renkleri: harita üstünde
 * `lib/factions.ts`'teki koyu artwork tonlarından daha iyi okunuyor.
 */
export const FOB_ZONE_M = 120

const FOBS: Record<string, { faction: string; color: string }> = {
  [wd('fob-lonestar')]:  { faction: 'Lonestar',  color: '#5fb8ff' },
  [wd('fob-valkyra')]:   { faction: 'Valkyra',   color: '#ff3b3b' },
  [wd('fob-manticore')]: { faction: 'Manticore', color: '#3ddc84' },
}

/** İşaretçi bir FOB mu — kayıtta yalnızca `src` durduğu için yoldan okunur. */
export function fobOf(src: string): { faction: string; color: string } | null {
  return FOBS[src] ?? null
}
